import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Alert, Box, IconButton, TextField, Typography } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { ModelId, ModelInfo } from 'chat-shared'
import { api } from '../api/client.js'
import { useChatSession } from '../chat/useChatSession.js'
import { MessageBubble } from '../components/MessageBubble.js'
import { StreamingBubble } from '../components/StreamingBubble.js'
import { ChatInput } from '../components/ChatInput.js'
import { ContextUsageIndicator } from '../components/ContextUsageIndicator.js'
import { readPreferredModel, storePreferredModel } from '../chat/modelPreference.js'

export const ChatPage = () => {
  const { chatId } = useParams<{ chatId: string }>()
  const navigate = useNavigate()
  const {
    chat,
    messages,
    streamingTurns,
    isStreaming,
    error,
    contextUsage,
    lastUsage,
    isCompacting,
    send,
    abort,
    rename,
    compact,
  } = useChatSession(chatId)
  const [models, setModels] = useState<Array<ModelInfo>>([])
  const [model, setModel] = useState<ModelId | undefined>(undefined)
  // undefined = nicht im Bearbeitungsmodus
  const [titleDraft, setTitleDraft] = useState<string | undefined>(undefined)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api.models().then(setModels).catch(() => setModels([]))
  }, [])

  useEffect(() => {
    // Zuletzt gewählte Auswahl (localStorage) hat Vorrang vor dem Modell,
    // mit dem der Chat ursprünglich angelegt wurde.
    if (chat) setModel(readPreferredModel() ?? chat.model)
  }, [chat])

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingTurns])

  if (!chatId) {
    navigate('/', { replace: true })
    return null
  }

  const saveTitle = async (): Promise<void> => {
    const title = (titleDraft ?? '').trim()
    if (title.length > 0 && title !== chat?.title) await rename(title)
    setTitleDraft(undefined)
  }

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box
        p={2}
        borderBottom="1px solid"
        borderColor="divider"
        display="flex"
        alignItems="center"
        gap={0.5}
      >
        {titleDraft === undefined ? (
          <>
            <Typography variant="h6" noWrap>
              {chat?.title ?? 'Chat wird geladen …'}
            </Typography>
            {chat?.access === 'owner' && (
              <IconButton
                size="small"
                aria-label="Chat umbenennen"
                onClick={() => setTitleDraft(chat.title)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </>
        ) : (
          <TextField
            size="small"
            autoFocus
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void saveTitle()
              if (e.key === 'Escape') setTitleDraft(undefined)
            }}
            onBlur={() => void saveTitle()}
            sx={{ width: '100%', maxWidth: 480 }}
          />
        )}
      </Box>
      <Box flex={1} overflow="auto" p={2}>
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} allMessages={messages} />
        ))}
        {streamingTurns.map((turn) => (
          <StreamingBubble key={turn.id} turn={turn} />
        ))}
        <div ref={scrollRef} />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mx: 2 }}>
          {error.message}
          {error.details && (
            <Box component="details" mt={0.5}>
              <Box component="summary" sx={{ cursor: 'pointer' }}>
                <Typography variant="caption" component="span">
                  Details
                </Typography>
              </Box>
              <Typography
                variant="caption"
                component="pre"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0, mt: 0.5 }}
              >
                {error.details}
              </Typography>
            </Box>
          )}
        </Alert>
      )}
      {chat?.access === 'read' && (
        <Alert severity="info" sx={{ mx: 2, mb: 1 }}>
          Nur Lesezugriff – du kannst diesen Chat nicht fortsetzen.
        </Alert>
      )}
      {model && chat?.access !== 'read' && (
        <ChatInput
          models={models}
          model={model}
          onModelChange={(value) => {
            if (models.some((entry) => entry.id === value)) {
              setModel(value)
              storePreferredModel(value)
            }
          }}
          onSend={(content) => send(content, model)}
          isStreaming={isStreaming}
          onAbort={abort}
          statusIndicator={
            <ContextUsageIndicator
              usage={contextUsage}
              lastUsage={lastUsage}
              onCompact={() => void compact(model)}
              isCompacting={isCompacting}
              canCompact={
                !isStreaming && messages.some((message) => message.role === 'assistant')
              }
            />
          }
        />
      )}
    </Box>
  )
}
